import moment from 'moment';
import { Button } from 'primereact/button';

import ImageComponent from 'shared/ImageComponent';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
/**
 * Component for rendering an individual comment with user info, content, and action buttons
 *
 * @param {Object} props - The component props
 * @param {Object} props.comment - The comment object containing id, text, user info, and time
 * @param {Function} props.onEdit - Function to call when edit button is clicked
 * @param {Function} props.onDelete - Function to call when delete button is clicked
 * @param {boolean} props.isUpdatingComment - Whether a comment is currently being updated
 * @param {string|null} props.deletingCommentId - ID of the comment currently being deleted (if any)
 * @param {string|null} props.editingComment - ID of the comment currently being edited (if any)
 * @param {string} props.editCommentText - Current text in the edit field
 * @param {Function} props.onSave - Function to call when save button is clicked
 * @param {Function} props.onCancel - Function to call when cancel button is clicked
 * @param {Function} props.setEditCommentText - Function to update the edit text state
 * @returns {JSX.Element} The rendered comment item
 */
export const CommentItem = ({
  comment,
  onEdit,
  onDelete,
  isUpdatingComment,
  deletingCommentId,
  editingComment,
  editCommentText,
  onSave,
  onCancel,
  setEditCommentText,
}) => {
  const isDeleting = deletingCommentId === comment.id;
  const userContext = useUserDetailsContext();
  const userTimezone = userContext?.userDetails?.timezone;
  return (
    <div className="d-flex align-items-start">
      <div>
        <ImageComponent
          filePath={comment.user.avatar}
          fileName={comment.user.name}
          className="me-2"
          avatarSize="small"
          style={{
            backgroundColor: '#2196F3',
            color: '#ffffff',
          }}
        />
      </div>
      <div className="flex-grow-1" style={{ position: 'relative', minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="text-bold F-size14">{comment.user.name}</div>
            <div className="F-size12 text-muted">{convertDateFromApiToUserTimezone(comment.time, userTimezone, 'MM/DD/YYYY hh:mm A')}</div>
          </div>
          {comment.editable && (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px', flexShrink: 0 }}>
              <i
                className={`pi pi-pencil pointer me-2 ${editingComment === comment.id ? 'text-primary' : ''}`}
                onClick={() => !isDeleting && onEdit(comment.id, comment.text)}
                style={{ opacity: isDeleting ? 0.5 : 1 }}
              ></i>
              {isDeleting ? (
                <i className="pi pi-spin pi-spinner text-muted"></i>
              ) : (
                <i className="pi pi-trash pointer text-danger" onClick={() => onDelete(comment.id)}></i>
              )}
            </div>
          )}
        </div>
        {editingComment === comment.id ? (
          <div className="mt-2">
            <TextSnippetWrapper
              type="textarea"
              rows="3"
              className="w-100 input-shadow"
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
            />
            <div className="d-flex justify-content-end mt-2">
              <Button type="reset" className="me-2 btn p-button-secondary outline" onClick={onCancel} disabled={isUpdatingComment}>
                Cancel
              </Button>
              <Button
                className="p-button-secondary me-2"
                type="submit"
                onClick={() => onSave(comment.id)}
                disabled={isUpdatingComment}
                loading={isUpdatingComment}
                loadingIcon="pi pi-spin pi-spinner"
                style={{ minWidth: '65px' }}
              >
                update
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="F-size14 comment-text mt-1"
            style={{
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {comment.text}
          </div>
        )}
      </div>
    </div>
  );
};
